import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Maps nav permission keys → route hrefs they grant access to.
 * This is the single source of truth for sidebar visibility.
 */
const navKeyToHrefs: Record<string, string[]> = {
  'nav:dashboard': ['/dashboard'],
  'nav:deals': ['/deals'],
  'nav:my-clients': ['/deals', '/clients'],
  'nav:ai-agent': ['/ai-agent'],
  'nav:dialer': ['/dialer'],
  'nav:my-field-meetings': ['/my-field-appointments'],
  'nav:my-field-appointments': ['/my-field-appointments'],
  'nav:field-sales-pipeline': ['/field-sales'],
  'nav:field-sales': ['/field-sales'],
  'nav:lists': ['/lists'],
  'nav:lists-import': ['/import', '/lists/import'],
  'nav:lists-import-advanced': ['/lists/import-advanced'],
  'nav:lists-statistics': ['/lists/statistics'],
  'nav:lists-statistics-advanced': ['/lists/statistics-advanced'],
  'nav:lists-assigner': ['/lists/assigner'],
  'nav:lists-scraper': ['/lists/scraper'],
  'nav:my-appointments': ['/my-appointments'],
  'nav:operations-calendar': ['/operations-calendar', '/operations/calendar'],
  'nav:work-call': ['/work'],
  'nav:work-tasks': ['/work/tasks'],
  'nav:work-analytics': ['/work/analytics'],
  'nav:work-finance-board': ['/work/finance-board'],
  'nav:work-finance-analytics': ['/work/finance-analytics'],
  'nav:services-manage': ['/services'],
  'nav:services-catalog': ['/services/catalog'],
  'nav:services-queues': ['/services/queues'],
  'nav:photo-queue': ['/photo-queue', '/services/photo-queue'],
  'nav:services-photo-queue': ['/photo-queue', '/services/photo-queue'],
  'nav:seo-board': ['/service-board/seo', '/services/board/seo'],
  'nav:services-board-seo': ['/service-board/seo', '/services/board/seo'],
  'nav:ads-board': ['/service-board/ads', '/services/board/ads'],
  'nav:services-board-ads': ['/service-board/ads', '/services/board/ads'],
  'nav:web-board': ['/service-board/web', '/services/board/web'],
  'nav:services-board-web': ['/service-board/web', '/services/board/web'],
  'nav:operations-board': ['/service-board/operations', '/services/board/operations'],
  'nav:services-board-operations': ['/service-board/operations', '/services/board/operations'],
  'nav:team-agents-teams': ['/team-agents', '/team-agents/teams'],
  'nav:team-agents-agents': ['/team-agents/agents'],
  'nav:team-agents-countries': ['/team-agents/countries'],
  'nav:team-agents-team-report': ['/team-agents/team-report'],
  'nav:team-agents-agent-report': ['/team-agents/agent-report'],
  'nav:team-agents-activity': ['/team-agents/activity'],
  'nav:team-agents-work-hours': ['/team-agents/work-hours'],
  'nav:team-bonus-daily': ['/team-bonus'],
  'nav:team-bonus-rules': ['/team-bonus/rules'],
  'nav:team-bonus-history': ['/team-bonus/history'],
  'nav:compensation': ['/team-agents'],
  'nav:my-compensation': ['/team-agents/agent-report'],
  'nav:payroll-salary': ['/team-agents'],
  'nav:payroll-attendance': ['/team-agents/work-hours'],
  'nav:payroll-tax-config': ['/settings'],
  'nav:payroll-bonus': ['/team-bonus/rules'],
  'nav:payroll-expenses': ['/team-agents'],
  'nav:payroll-summary': ['/team-agents/team-report'],
  'nav:finance-orders': ['/finance/orders'],
  'nav:finance-overview': ['/finance/overview', '/finance'],
  'nav:finance-operating': ['/finance/operating'],
  'nav:finance-cash': ['/finance/operating'],
  'nav:finance-costs': ['/finance/operating'],
  'nav:finance-country-costs': ['/finance/country-costs'],
  'nav:finance-vat': ['/finance/proforma'],
  'nav:finance-payment-flow': ['/finance/payment-flow'],
  'nav:finance-investments': ['/finance/investments'],
  'nav:finance-debt': ['/finance'],
  'nav:finance-loans': ['/finance'],
  'nav:finance-anomalies': ['/finance'],
  'nav:finance-bank-accounts': ['/finance'],
  'nav:finance-transactions': ['/finance'],
  'nav:finance-multi-country': ['/finance/multi-country'],
  'nav:finance-owner-dashboard': ['/finance/owner-dashboard'],
  'nav:finance-periods': ['/finance'],
  'nav:finance-forecast': ['/finance/forecast'],
  'nav:finance-proforma': ['/finance/proforma'],
  'nav:analytics-owner': ['/finance/owner-dashboard'],
  'nav:analytics-profit': ['/finance/owner-dashboard'],
  'nav:analytics-leaderboard': ['/team-agents'],
  'nav:analytics-utilization': ['/finance/owner-dashboard'],
  'nav:analytics-cancellations': ['/reports'],
  'nav:analytics-bottlenecks': ['/reports'],
  'nav:reports-default': ['/reports'],
  'nav:reports-dynamic': ['/reports/dynamic'],
  'nav:reports-chart': ['/reports/chart'],
  'nav:reports-agent-bonus': ['/reports/agent-bonus'],
  'nav:reports-status-history': ['/reports/status-history'],
  'nav:reports-personals': ['/reports/personals'],
  'nav:reports-trash-cancel': ['/reports/trash-cancel'],
  'nav:reports-cant-call': ['/reports/cant-call'],
  'nav:reports-paid': ['/reports/paid'],
  'nav:ai-insights': ['/insights'],
  'nav:insights': ['/insights'],
  'nav:my-insights': ['/my-insights'],
  'nav:notifications-inbox': ['/notifications'],
  'nav:notifications-send': ['/notifications/send'],
  'nav:kitchen-order': ['/kitchen'],
  'nav:kitchen-my-orders': ['/kitchen/my-orders'],
  'nav:kitchen-manager': ['/kitchen/manager'],
  'nav:kitchen-orders': ['/kitchen/orders'],
  'nav:kitchen-balance': ['/kitchen/balance'],
  'nav:kitchen-transactions': ['/kitchen/transactions'],
  'nav:kitchen-history': ['/kitchen/history'],
  'nav:kitchen-report': ['/kitchen/report'],
  'nav:settings-profile': ['/settings'],
  'nav:settings-permissions': ['/settings/permissions'],
  'nav:settings-countries': ['/settings/countries'],
  'nav:settings-currencies': ['/settings/currencies'],
  'nav:duplicate-manager': ['/duplicate-manager'],
  'nav:user-management': ['/users'],
};

/**
 * Legacy mapping kept for backward compatibility.
 */
const legacySectionToHref: Record<string, string> = {
  'dashboard': '/dashboard',
  'work-screen': '/work',
  'clients': '/deals',
  'lists': '/lists',
  'deals': '/deals',
  'ai-agent': '/ai-agent',
  'dialer': '/dialer',
  'services': '/services',
  'team-bonus': '/team-bonus',
  'team-agents': '/team-agents',
  'payroll': '/team-agents',
  'finance': '/finance',
  'reports': '/reports',
  'kitchen': '/kitchen',
  'notifications': '/notifications',
  'settings': '/settings',
  'user-management': '/users',
  'work-screen:call': '/work',
  'work-screen:tasks': '/work/tasks',
  'work-screen:analytics': '/work/analytics',
  'work-screen:finance-board': '/work/finance-board',
  'work-screen:finance-analytics': '/work/finance-analytics',
  'lists:lead-lists': '/lists',
  'lists:import': '/lists/import',
  'lists:import-advanced': '/lists/import-advanced',
  'lists:statistics': '/lists/statistics',
  'lists:statistics-advanced': '/lists/statistics-advanced',
  'lists:assigner': '/lists/assigner',
  'lists:scraper': '/lists/scraper',
  'reports:paid': '/reports/paid',
  'services:manage': '/services',
  'services:catalog': '/services/catalog',
  'team-bonus:daily': '/team-bonus',
  'team-bonus:rules': '/team-bonus/rules',
  'team-bonus:history': '/team-bonus/history',
  'team-agents:teams': '/team-agents/teams',
  'team-agents:agents': '/team-agents/agents',
  'team-agents:countries': '/team-agents/countries',
  'team-agents:team-report': '/team-agents/team-report',
  'team-agents:agent-report': '/team-agents/agent-report',
  'team-agents:activity': '/team-agents/activity',
  'team-agents:work-hours': '/team-agents/work-hours',
  'payroll:salary': '/team-agents',
  'payroll:bonus': '/team-bonus/rules',
  'payroll:expenses': '/team-agents',
  'payroll:summary': '/team-agents/team-report',
  'payroll:attendance': '/team-agents/work-hours',
  'payroll:tax-config': '/settings',
  'finance:overview': '/finance/overview',
  'finance:orders': '/finance/orders',
  'finance:operating': '/finance',
  'finance:costs': '/finance/operating',
  'finance:cash': '/finance/operating',
  'finance:debt': '/finance',
  'finance:investments': '/finance/investments',
  'finance:loans': '/finance',
  'finance:anomalies': '/finance',
  'finance:country-costs': '/finance/country-costs',
  'finance:vat': '/finance/proforma',
  'finance:payment-flow': '/finance/payment-flow',
  'reports:default': '/reports',
  'reports:dynamic': '/reports/dynamic',
  'reports:chart': '/reports/chart',
  'reports:bonus': '/reports/agent-bonus',
  'reports:status-history': '/reports/status-history',
  'reports:personals': '/reports/personals',
  'reports:trash-cancel': '/reports/trash-cancel',
  'reports:cant-call': '/reports/cant-call',
  'kitchen:order': '/kitchen',
  'kitchen:my-orders': '/kitchen/my-orders',
  'kitchen:manager': '/kitchen/manager',
  'kitchen:orders': '/kitchen/orders',
  'kitchen:balance': '/kitchen/balance',
  'kitchen:transactions': '/kitchen/transactions',
  'kitchen:history': '/kitchen/history',
  'kitchen:report': '/kitchen/report',
  'notifications:inbox': '/notifications',
  'notifications:send': '/notifications/send',
  'settings:profile': '/settings',
  'settings:permissions': '/settings/permissions',
  'settings:countries': '/settings/countries',
  'settings:currencies': '/settings/currencies',
  'compensation': '/team-agents',
  'my-compensation': '/team-agents/agent-report',
};

export interface AllowedRoutesResult {
  /** null = admin (everything allowed). Set = resolved permissions. */
  routes: Set<string> | null;
  /** true while queries are still fetching */
  isLoading: boolean;
}

export function useAllowedRoutes(): AllowedRoutesResult {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const { data: newPermissions, isLoading: loadingNew } = useQuery({
    queryKey: ['role-permission-defaults-nav', user?.role],
    queryFn: async () => {
      if (!user?.role) return null;
      const { data, error } = await supabase
        .from('role_permission_defaults')
        .select('permission_key, access_level')
        .eq('role', user.role)
        .like('permission_key', 'nav:%');
      if (error) throw error;
      return data as { permission_key: string; access_level: string }[] | null;
    },
    enabled: !!user?.role && !isAdmin,
    staleTime: 300_000,
  });

  const { data: legacySections, isLoading: loadingLegacy } = useQuery({
    queryKey: ['role-permissions-sidebar', user?.role],
    queryFn: async () => {
      if (!user?.role) return null;
      const { data, error } = await supabase
        .from('role_permissions')
        .select('allowed_sections')
        .eq('role', user.role)
        .maybeSingle();
      if (error) throw error;
      return data?.allowed_sections as string[] | null;
    },
    enabled: !!user?.role && !isAdmin,
    staleTime: 300_000,
  });

  // Admin always sees everything
  if (isAdmin) return { routes: null, isLoading: false };

  const isLoading = loadingNew || loadingLegacy;

  // Try new permission system first
  if (newPermissions && newPermissions.length > 0) {
    const allowed = new Set<string>();
    for (const perm of newPermissions) {
      if (perm.access_level === 'none') continue;
      const hrefs = navKeyToHrefs[perm.permission_key];
      if (hrefs) hrefs.forEach(h => allowed.add(h));
    }
    // Even if allowed is empty (all 'none'), return it — that's a resolved state
    return { routes: allowed, isLoading: false };
  }

  // Fallback to legacy allowed_sections
  if (legacySections && legacySections.length > 0) {
    const allowed = new Set<string>();
    for (const section of legacySections) {
      const href = legacySectionToHref[section];
      if (href) allowed.add(href);
    }
    return { routes: allowed, isLoading: false };
  }

  // Still loading — return null routes with loading=true
  if (isLoading) {
    return { routes: null, isLoading: true };
  }

  // Resolved but no permissions configured → empty set (deny all nav except settings profile)
  const minimal = new Set<string>(['/settings']);
  return { routes: minimal, isLoading: false };
}
